"use client";

import React, { Dispatch, SetStateAction, FC, useState, useMemo } from "react";
import { useI18n } from "@/i18n/client";
import { ProductData } from "@/lib/5874/types";
import useStoreKeys from "@/hooks/useStoreKeys";
import CompressedImage from "@/components/UI/CompressedImage/CompressedImage";
import FormattedPrice from "@/components/UI/FormattedPrice/FormattedPrice";
import QuantitySelector from "@/components/Cart/QuantitySelector/QuantitySelector";
import StockAvailability from "@/components/StockAvailability/StockAvailability";
import Link from "next/link";
import styles from "./ProductKitItem.module.scss";
import PlaceholderImage from "@/app/assets/images/img/placeholder.png";
import mapProductOptions from "@/hooks/mapProductOptions";

interface ProductKitItemProps {
	product: ProductData;
	isFixedPrice: boolean;
	allKitProducts?: ProductData[];
	setKitProductData?: Dispatch<SetStateAction<ProductData[]>>;
	parentIsNotPurchasable: boolean;
}

const ProductKitItem: FC<ProductKitItemProps> = ({
	product,
	isFixedPrice,
	allKitProducts,
	setKitProductData,
	parentIsNotPurchasable,
}) => {
	const t = useI18n();
	const { store, lang } = useStoreKeys();
	const [quantity, setQuantity] = useState(product.kitQuantity);

	const productSpecifications = useMemo(() => {
		return mapProductOptions([product]);
	}, [product]);

	const updateKitItemQuantity = (quantity: number) => {
		setQuantity(quantity);

		if (!allKitProducts || !setKitProductData) return;

		const updatedKitData = allKitProducts.map((kitProduct) => {
			if (kitProduct.id === product.id) {
				return {
					...kitProduct,
					kitQuantity: quantity,
				};
			}

			return kitProduct;
		});

		if (updatedKitData) setKitProductData(updatedKitData);
	};

	return (
		<div className={styles.product}>
			<div className={styles.inner}>
				<CompressedImage
					src={product?.media?.images[0]?.url.thumbnail || PlaceholderImage.src}
					height={80}
					width={80}
					altText={product.name}
					containerClass={styles.image}
					withNextImage={false}
				/>
				<div className={styles.details}>
					<span className={styles.name}>{product.name}</span>
					<span className={styles.sku}>{product.sku}</span>
					{productSpecifications && productSpecifications.length > 0 && (
						<div className={styles.specs}>
							{productSpecifications?.map((spec) => (
								<div key={spec.label} className={styles.spec}>
									<span className={styles.label}>{spec.label}:</span>
									<span className={styles.value}>{spec.options[0].label}</span>
								</div>
							))}
						</div>
					)}
					{isFixedPrice && (
						<span className={styles.quantity}>
							{t("cart.quantity")}: {quantity}
						</span>
					)}
					{!isFixedPrice ||
						(!product.isNotPurchasable && (
							<FormattedPrice
								className={styles.price}
								price={(product?.pricesExTax?.basePrice?.value || 0) * quantity}
							/>
						))}
					<Link
						href={`/${store}/${lang}/${product.path}`}
						className={styles.link}
					>
						{t("text.showDetails")}
					</Link>
				</div>
			</div>
			{!isFixedPrice && !parentIsNotPurchasable && (
				<div className={styles.actions}>
					<QuantitySelector
						minValue={0}
						quantity={quantity}
						onChange={updateKitItemQuantity}
						isDisabled={product.isNotPurchasable}
					/>
					<StockAvailability product={product} />
				</div>
			)}
		</div>
	);
};

export default ProductKitItem;
